import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../lib/supabase";
import { categorizeProduct } from "../lib/categorizeProduct";
import {
  isHeicFile,
  isWebpFile,
  isAvifFile,
  UNSUPPORTED_FORMAT_MESSAGE,
  UnsupportedImageFormatError,
  normalizeImageForUpload,
} from "../utils/heicConvert";
import {
  baseNameFromFileName,
  groupItemsByStem,
  type FileGroup,
} from "../utils/groupFilesByStem";
import { buildThumbnailBlob } from "../utils/thumbnail";

export const BULK_MAX_FILE_SIZE = 100 * 1024 * 1024; // 100 MB
export const BULK_CONCURRENCY = 3;

export type BulkItemStatus = "pending" | "uploading" | "success" | "error";

export type BulkItem = {
  id: string;
  file: File;
  previewUrl: string;
  nombre: string;
  status: BulkItemStatus;
  error?: string;
};

export const isFreeLikePlan = (plan: string) => {
  const p = (plan || "").toLowerCase();
  return p === "gratis" || p === "free" || p === "";
};

export const getBulkPlanLimit = (plan: string) =>
  isFreeLikePlan(plan) ? 5 : 20;

interface Args {
  idCompany: string | null;
  userPlan: string;
  onSuccess?: () => Promise<void> | void;
  brandId?: string | null;
}

export type SaveAllResult = "all-success" | "partial" | "none" | "error";

export function useBulkUpload({ idCompany, userPlan, onSuccess, brandId }: Args) {
  const [items, setItems] = useState<BulkItem[]>([]);
  const [startingN, setStartingN] = useState(1);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [error, setError] = useState<string | null>(null);
  const [convertingHeic, setConvertingHeic] = useState(false);
  // Toggle "Usar nombres de archivo": cuando está activo, los items se
  // agrupan por stem (groupItemsByStem) y cada grupo se sube como un
  // producto distinto, con el primer archivo como imagen principal y el
  // resto como imágenes adicionales. Los nombres se prepoblan desde el
  // filename; el override por grupo se guarda en `groupNameOverrides`.
  const [useFileNames, setUseFileNames] = useState(false);
  const [groupNameOverrides, setGroupNameOverrides] = useState<
    Record<string, string>
  >({});
  const fileInputRef = useRef<HTMLInputElement>(null);
  const limit = getBulkPlanLimit(userPlan);

  // Grupos derivados — sólo se calculan cuando el toggle está activo.
  const groups = useMemo<FileGroup<BulkItem>[]>(
    () =>
      useFileNames ? groupItemsByStem(items, (it) => it.file.name) : [],
    [items, useFileNames],
  );

  /** Nombre a mostrar para un grupo: si el user lo editó, su override;
   *  caso contrario el `baseName` derivado del filename. */
  const groupDisplayName = useCallback(
    (group: FileGroup<BulkItem>) =>
      groupNameOverrides[group.key]?.trim() || group.baseName,
    [groupNameOverrides],
  );

  // Compute starting N whenever idCompany changes (so names default Nuevo producto N)
  useEffect(() => {
    if (!idCompany) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase
          .from("productos")
          .select("nombre")
          .eq("id_company", idCompany)
          .ilike("nombre", "Nuevo producto %");
        if (cancelled) return;
        const regex = /^Nuevo producto (\d+)$/;
        const maxN = (data || []).reduce((max: number, row: any) => {
          const name = (row?.nombre || "").trim();
          const m = regex.exec(name);
          if (!m) return max;
          const n = parseInt(m[1], 10);
          return Number.isFinite(n) && n > max ? n : max;
        }, 0);
        setStartingN(maxN + 1);
      } catch {
        setStartingN(1);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [idCompany]);

  const openFilePicker = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Núcleo de procesamiento de archivos, usable tanto desde el file picker
  // (handleSelectFiles) como desde drag & drop (e.dataTransfer.files).
  const addFiles = useCallback(
    async (incoming: FileList | File[]) => {
      const rawList = Array.from(incoming);
      if (rawList.length === 0) return;
      setError(null);
      // Spinner "Procesando imágenes…" solo si hay algo que convertir. AVIF
      // cuenta: la conversión por canvas de una foto de cámara moderna no es
      // instantánea.
      const hasConvertible = rawList.some(
        (f) => isHeicFile(f) || isWebpFile(f) || isAvifFile(f),
      );
      if (hasConvertible) setConvertingHeic(true);

      const processed: { file: File; previewUrl: string }[] = [];
      for (const f of rawList) {
        try {
          // `normalizeImageForUpload` es el ÚNICO lugar donde vive la política
          // de formatos: rechaza RAW/TIFF/GIF/SVG (ADR-590) y convierte
          // HEIC/HEIF, AVIF y WEBP (ADR-589) a JPEG. Antes esto se gateaba a
          // mano acá con `isHeicFile || isWebpFile`, y por eso el AVIF se subía
          // crudo (ADR-687).
          const ffile = await normalizeImageForUpload(f);
          // El preview se arma del archivo YA normalizado: así lo que se ve es
          // exactamente lo que se va a subir, y siempre es un formato que el
          // browser sabe pintar.
          const previewUrl = URL.createObjectURL(ffile);
          if (ffile.size > BULK_MAX_FILE_SIZE) {
            URL.revokeObjectURL(previewUrl);
            // Mismo criterio que el catch: nunca descartar en silencio.
            setError(
              `"${f.name}" supera el tamaño máximo de 100 MB y no se agregó.`,
            );
            continue;
          }
          processed.push({ file: ffile, previewUrl });
        } catch (err) {
          // Se saltea ESE archivo y el resto de la tanda sigue — pero SIEMPRE
          // con mensaje al user: antes un fallo de conversión (ej. el decoder
          // del browser no banca AVIF) omitía la imagen en silencio y "desaparecía"
          // de la carga sin explicación.
          if (err instanceof UnsupportedImageFormatError) {
            setError(UNSUPPORTED_FORMAT_MESSAGE);
          } else {
            console.warn("Error procesando archivo, se omite:", f.name, err);
            setError(
              `No pudimos procesar "${f.name}" en este navegador. Convertila a JPG o PNG y volvé a intentar.`,
            );
          }
        }
      }
      setConvertingHeic(false);

      setItems((prev) => {
        const available = Math.max(0, limit - prev.length);
        const toAdd = processed.slice(0, available);
        const discarded = processed.slice(available);
        discarded.forEach((d) => {
          if (d.previewUrl.startsWith("blob:"))
            URL.revokeObjectURL(d.previewUrl);
        });
        if (processed.length > available) {
          setError(
            `Llegaste al límite de ${limit} productos por carga. ${
              processed.length - available
            } imagen(es) no se agregaron.`,
          );
        }
        const newItems: BulkItem[] = toAdd.map(({ file, previewUrl }, idx) => ({
          id: `${Date.now()}_${idx}_${Math.random().toString(36).slice(2, 8)}`,
          file,
          previewUrl,
          nombre: "",
          status: "pending" as const,
        }));
        return [...prev, ...newItems];
      });
    },
    [limit],
  );

  const handleSelectFiles = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      await addFiles(e.target.files || []);
      if (fileInputRef.current) fileInputRef.current.value = "";
    },
    [addFiles],
  );

  const removeItem = useCallback((id: string) => {
    setItems((prev) => {
      const it = prev.find((p) => p.id === id);
      if (it && it.previewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(it.previewUrl);
      }
      return prev.filter((p) => p.id !== id);
    });
  }, []);

  const updateName = useCallback((id: string, name: string) => {
    setItems((prev) =>
      prev.map((p) => (p.id === id ? { ...p, nombre: name } : p)),
    );
  }, []);

  const defaultNameFor = useCallback(
    (index: number) => `Nuevo producto ${startingN + index}`,
    [startingN],
  );

  const clearAll = useCallback(() => {
    setItems((prev) => {
      prev.forEach((it) => {
        if (it.previewUrl.startsWith("blob:"))
          URL.revokeObjectURL(it.previewUrl);
      });
      return [];
    });
    setProgress({ done: 0, total: 0 });
    setError(null);
    setGroupNameOverrides({});
    setUseFileNames(false);
  }, []);

  /** Versión optimizada para el bucket `opt/`. Best-effort: null si falla. */
  const buildOptimizedBlob = useCallback(
    (file: File): Promise<Blob | null> => buildThumbnailBlob(file),
    [],
  );

  /** Sube un producto con N archivos. El primero queda como imagen
   *  principal; los siguientes se insertan en `productos_imagenes` con
   *  `principal: false` y `orden` incremental. Si UNO de los archivos
   *  falla en subir, todo el producto se marca como error (el row de
   *  producto/variante queda creado pero sin imágenes — el user verá
   *  el row con error y podrá reintentar). */
  const uploadProductWithFiles = useCallback(
    async (
      files: File[],
      effectiveName: string,
      userId: string,
    ): Promise<{ ok: boolean; error?: string }> => {
      try {
        if (!idCompany) throw new Error("No se encontró la empresa");
        if (files.length === 0) throw new Error("Sin archivos");

        const productoPayload: Record<string, any> = {
          id_company: idCompany,
          nombre: effectiveName,
          tiene_variantes: false,
        };
        if (brandId) {
          productoPayload.brand_id = brandId;
        }
        const { data: producto, error: productoError } = await supabase
          .from("productos")
          .insert(productoPayload)
          .select("id")
          .single();
        if (productoError) throw productoError;
        const productId = String(producto.id);

        const { data: variant, error: variantError } = await supabase
          .from("variantes")
          .insert({
            id_company: idCompany,
            nombre: effectiveName,
            activo: true,
            product_id: productId,
          })
          .select("id")
          .single();
        if (variantError) throw variantError;
        const variantId = variant.id as string;

        const timestamp = Date.now();
        // Subimos archivos secuencialmente para mantener nombres únicos y
        // ordenar el `orden` de manera predecible. El timestamp + el idx
        // evita colisiones si dos archivos comparten nombre exacto.
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          const sanitizedName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
          const fileBaseName = `${timestamp}_${i}_${sanitizedName}`;
          const fullPath = `${idCompany}/${variantId}/full/${fileBaseName}`;
          const { error: fullErr } = await supabase.storage
            .from("productos")
            .upload(fullPath, file);
          if (fullErr) throw fullErr;

          const optBlob = await buildOptimizedBlob(file);
          if (optBlob) {
            const optPath = `${idCompany}/${variantId}/opt/${fileBaseName}`;
            const { error: optErr } = await supabase.storage
              .from("productos")
              .upload(optPath, optBlob);
            if (optErr)
              console.warn("Optimized upload failed for", fileBaseName, optErr);
          }

          const { error: imgErr } = await supabase
            .from("productos_imagenes")
            .insert({
              id_company: idCompany,
              variante_id: variantId,
              url: fileBaseName,
              principal: i === 0,
              orden: i + 1,
            });
          if (imgErr) throw imgErr;
        }

        categorizeProduct(String(productId), {
          modo: "crear",
          completarNombre: false,
          userId,
        });

        return { ok: true };
      } catch (err: any) {
        return {
          ok: false,
          error: err?.message || "Error al guardar el producto",
        };
      }
    },
    [idCompany, brandId, buildOptimizedBlob],
  );

  /** Edita el nombre que se va a usar para un grupo (solo aplica cuando
   *  el toggle "Usar nombres de archivo" está activo). */
  const updateGroupName = useCallback((key: string, name: string) => {
    setGroupNameOverrides((prev) => ({ ...prev, [key]: name }));
  }, []);

  /** Elimina todos los items pertenecientes a un grupo. */
  const removeGroup = useCallback((key: string) => {
    setGroupNameOverrides((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setItems((prev) => {
      // Recalculamos los grupos sobre la lista actual y filtramos los
      // items que pertenecen a la `key` provista. Hacerlo así (vs guardar
      // el key dentro del item) nos evita desincronización si el set de
      // items cambia entre renders.
      const allGroups = groupItemsByStem(prev, (it) => it.file.name);
      const target = allGroups.find((g) => g.key === key);
      const drop = new Set(target?.files.map((it) => it.id) || []);
      const removed = prev.filter((p) => drop.has(p.id));
      removed.forEach((it) => {
        if (it.previewUrl.startsWith("blob:")) URL.revokeObjectURL(it.previewUrl);
      });
      return prev.filter((p) => !drop.has(p.id));
    });
  }, []);

  const saveAll = useCallback(async (): Promise<SaveAllResult> => {
    if (items.length === 0 || saving) return "none";
    setSaving(true);
    setError(null);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("No se encontró el usuario");

      // Recalculate startingN at save time to avoid collisions
      let baseN = startingN;
      try {
        const { data } = await supabase
          .from("productos")
          .select("nombre")
          .eq("id_company", idCompany)
          .ilike("nombre", "Nuevo producto %");
        const regex = /^Nuevo producto (\d+)$/;
        const maxN = (data || []).reduce((max: number, row: any) => {
          const name = (row?.nombre || "").trim();
          const m = regex.exec(name);
          if (!m) return max;
          const n = parseInt(m[1], 10);
          return Number.isFinite(n) && n > max ? n : max;
        }, 0);
        baseN = maxN + 1;
        setStartingN(baseN);
      } catch {}

      // Snapshot del modo actual: si el user togglea durante el save no
      // pasa nada — usamos lo que había al apretar Guardar.
      const savingByGroups = useFileNames;
      // Cada "task" representa un producto a crear (1+ archivos).
      type SaveTask = { itemIds: string[]; files: File[]; name: string };
      const tasks: SaveTask[] = savingByGroups
        ? groupItemsByStem(items, (it) => it.file.name).map((group) => ({
            itemIds: group.files.map((it) => it.id),
            files: group.files.map((it) => it.file),
            name:
              (groupNameOverrides[group.key]?.trim()) || group.baseName,
          }))
        : items.map((it, idx) => ({
            itemIds: [it.id],
            files: [it.file],
            name: it.nombre.trim() || `Nuevo producto ${baseN + idx}`,
          }));

      setProgress({ done: 0, total: tasks.length });

      const taskIds = new Set(tasks.flatMap((t) => t.itemIds));
      setItems((prev) =>
        prev.map((p) =>
          taskIds.has(p.id)
            ? { ...p, status: "uploading" as const, error: undefined }
            : p,
        ),
      );

      const queue = [...tasks];
      let done = 0;
      const results: { itemIds: string[]; ok: boolean; error?: string }[] = [];

      const worker = async () => {
        while (queue.length > 0) {
          const task = queue.shift();
          if (!task) return;
          const res = await uploadProductWithFiles(
            task.files,
            task.name,
            user.id,
          );
          done += 1;
          setProgress({ done, total: tasks.length });
          const idSet = new Set(task.itemIds);
          setItems((prev) =>
            prev.map((p) =>
              idSet.has(p.id)
                ? {
                    ...p,
                    status: res.ok ? "success" : "error",
                    error: res.error,
                  }
                : p,
            ),
          );
          results.push({ itemIds: task.itemIds, ok: res.ok, error: res.error });
        }
      };

      const workers = Array.from(
        { length: Math.min(BULK_CONCURRENCY, tasks.length) },
        () => worker(),
      );
      await Promise.all(workers);

      const successCount = results.filter((r) => r.ok).length;
      const failCount = results.length - successCount;

      try {
        if (onSuccess) await onSuccess();
      } catch (e) {
        console.warn("onSuccess refresh failed:", e);
      }

      if (failCount === 0) {
        return "all-success";
      } else {
        setError(
          `${successCount} producto(s) creado(s). ${failCount} fallaron — revisalos abajo y reintentá.`,
        );
        // Eliminar items de los productos que se subieron OK, y dejar
        // pending los items que pertenecen a productos que fallaron para
        // que el user pueda reintentar.
        const okItemIds = new Set(
          results.filter((r) => r.ok).flatMap((r) => r.itemIds),
        );
        setItems((prev) =>
          prev
            .filter((p) => !okItemIds.has(p.id))
            .map((p) => ({ ...p, status: "pending" as const })),
        );
        setProgress({ done: 0, total: 0 });
        return "partial";
      }
    } catch (err: any) {
      setError(err?.message || "Error inesperado al guardar");
      return "error";
    } finally {
      setSaving(false);
    }
  }, [
    items,
    saving,
    idCompany,
    startingN,
    useFileNames,
    groupNameOverrides,
    uploadProductWithFiles,
    onSuccess,
  ]);

  return {
    items,
    limit,
    saving,
    progress,
    error,
    convertingHeic,
    fileInputRef,
    startingN,
    openFilePicker,
    addFiles,
    handleSelectFiles,
    removeItem,
    updateName,
    defaultNameFor,
    clearAll,
    saveAll,
    isFreeLikePlan: isFreeLikePlan(userPlan),
    // ── Modo "Usar nombres de archivo" ────────────────────────────────
    useFileNames,
    setUseFileNames,
    groups,
    groupDisplayName,
    updateGroupName,
    removeGroup,
    /** Helper exportado por si el caller quiere prepoblar/derivar el
     *  baseName desde un filename sin armar todo un grupo. */
    baseNameFromFileName,
  };
}
